import { useState, useEffect } from 'react'
import { PaymentForm as SquarePaymentForm, CreditCard, Ach } from 'react-square-web-payments-sdk'
import axios from 'axios'

const PaymentForm = ({
  amount,
  description,
  onSuccess,
  onCancel,
  customerEmail,
  customerName,
  referenceId,
  metadata,
}) => {
  const [paymentMethod, setPaymentMethod] = useState('card') // 'card' or 'ach'
  const [amounts, setAmounts] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [calculatingFee, setCalculatingFee] = useState(true)

  // Calculate processing fee when amount or payment method changes
  useEffect(() => {
    const calculateFee = async () => {
      try {
        setCalculatingFee(true)
        const response = await axios.post('/api/payments/calculate-fee', {
          amount,
          paymentMethod
        })
        setAmounts(response.data.amounts)
      } catch (err) {
        console.error('Error calculating fee:', err)
        setError('Failed to calculate processing fee')
      } finally {
        setCalculatingFee(false)
      }
    }

    if (amount) {
      calculateFee()
    }
  }, [amount, paymentMethod])

  const handlePayment = async (token) => {
    try {
      setLoading(true)
      setError(null)

      // Extract the token string from the token object
      const sourceId = typeof token === 'string' ? token : token?.token

      const response = await axios.post('/api/payments/process', {
        sourceId: sourceId,
        amount: amount,
        description,
        customerEmail,
        customerName,
        referenceId,
        metadata,
        paymentMethod,
      })

      console.log('Payment response:', response.data)

      if (response.data.success) {
        console.log('Payment successful! Calling onSuccess callback...')
        onSuccess(response.data.payment)
      } else {
        console.log('Payment failed:', response.data.message)
        setError(response.data.message || 'Payment failed')
      }
    } catch (err) {
      console.error('Payment error:', err)
      setError(err.response?.data?.message || 'Payment processing failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleError = (error) => {
    console.error('Square payment error:', error)
    setError('Payment form error. Please check your payment details and try again.')
  }

  // Debug: Log Square configuration on mount
  useEffect(() => {
    console.log('Square Configuration Check:')
    console.log('   Application ID:', import.meta.env.VITE_SQUARE_APPLICATION_ID || 'MISSING')
    console.log('   Location ID:', import.meta.env.VITE_SQUARE_LOCATION_ID || 'MISSING')
  }, [])

  if (calculatingFee) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Calculating fees...</p>
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Payment Method Selection */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Payment Method</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* ACH Payment Option */}
          <button
            type="button"
            onClick={() => setPaymentMethod('ach')}
            className={`p-4 border-2 rounded-lg text-left transition-all ${
              paymentMethod === 'ach'
                ? 'border-blue-600 bg-blue-50'
                : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
                <span className="font-semibold text-gray-900">Bank Account (ACH)</span>
              </div>
              {paymentMethod === 'ach' && (
                <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              )}
            </div>
            <p className="text-sm text-gray-600 mb-1">Direct bank transfer via Plaid</p>
            <p className="text-sm font-semibold text-green-600">No processing fee</p>
          </button>

          {/* Card Payment Option */}
          <button
            type="button"
            onClick={() => setPaymentMethod('card')}
            className={`p-4 border-2 rounded-lg text-left transition-all ${
              paymentMethod === 'card'
                ? 'border-blue-600 bg-blue-50'
                : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
                <span className="font-semibold text-gray-900">Credit/Debit Card</span>
              </div>
              {paymentMethod === 'card' && (
                <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              )}
            </div>
            <p className="text-sm text-gray-600 mb-1">Visa, Mastercard, Discover, Amex</p>
            <p className="text-sm font-semibold text-gray-600">3% processing fee</p>
          </button>
        </div>
      </div>

      {/* Payment Summary */}
      {amounts && (
        <div className="bg-gray-50 rounded-lg p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Summary</h3>
          <div className="space-y-2">
            <div className="flex justify-between text-gray-700">
              <span>Subtotal:</span>
              <span>${amounts.subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-gray-700">
              <span>Processing Fee {paymentMethod === 'card' ? '(3%)' : '(0%)'}:</span>
              <span>${amounts.processingFee.toFixed(2)}</span>
            </div>
            <div className="border-t border-gray-300 pt-2 mt-2">
              <div className="flex justify-between text-lg font-bold text-gray-900">
                <span>Total:</span>
                <span>${amounts.total.toFixed(2)}</span>
              </div>
            </div>
          </div>
          {description && (
            <p className="mt-4 text-sm text-gray-600 border-t border-gray-200 pt-3">
              {description}
            </p>
          )}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-md mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Payment Form */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          {paymentMethod === 'ach' ? 'Bank Account Information' : 'Card Information'}
        </h3>

        <SquarePaymentForm
          applicationId={import.meta.env.VITE_SQUARE_APPLICATION_ID}
          cardTokenizeResponseReceived={handlePayment}
          locationId={import.meta.env.VITE_SQUARE_LOCATION_ID}
          createPaymentRequest={() => ({
            countryCode: 'US',
            currencyCode: 'USD',
            total: {
              amount: amounts?.total?.toFixed(2) || '0.00',
              label: 'Total',
            },
          })}
        >
          {paymentMethod === 'card' ? (
            <CreditCard
              includeInputLabels
              buttonProps={{
                css: {
                  backgroundColor: '#2563eb',
                  fontSize: '16px',
                  color: '#fff',
                  '&:hover': {
                    backgroundColor: '#1d4ed8',
                  },
                },
              }}
              style={{
                input: {
                  fontSize: '16px',
                  fontFamily: 'inherit',
                },
                'input::placeholder': {
                  color: '#9CA3AF',
                },
              }}
              callbacks={{
                cardNonceResponseReceived: handlePayment,
                unsupportedBrowserDetected: () => {
                  setError('Your browser is not supported. Please use a modern browser.')
                },
                paymentFormLoaded: () => {
                  console.log('Card payment form loaded')
                },
              }}
              onError={handleError}
            />
          ) : (
            <Ach
              accountHolderName={customerName || ''}
              redirectURI={window.location.href}
              transactionId={referenceId || `LFST-${Date.now()}`}
            />
          )}
        </SquarePaymentForm>

        {paymentMethod === 'ach' && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              You'll be prompted to securely verify your bank account through Plaid.
              ACH payments typically take 2-3 business days to process.
            </p>
          </div>
        )}

        {/* Cancel Button */}
        <div className="mt-4">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="w-full px-6 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>

      {/* Security Notice */}
      <div className="mt-4 text-center text-sm text-gray-600">
        <div className="flex items-center justify-center gap-2">
          <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
              clipRule="evenodd"
            />
          </svg>
          <span>Secure payment powered by Square</span>
        </div>
      </div>
    </div>
  )
}

export default PaymentForm
